import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star } from 'lucide-react';
import { format } from 'date-fns';

interface Review {
  id: number;
  rating: number;
  comment?: string;
  created_at: string;
  highlighted?: boolean;
  service_id: number;
  services?: {
    name: string;
    location?: string;
    image_url?: string;
  };
}

interface ReviewCardProps {
  review: Review;
  showServiceInfo?: boolean;
  onClick?: () => void;
  className?: string;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({ 
  review, 
  showServiceInfo = true, 
  onClick,
  className = ""
}) => {
  return (
    <Card 
      key={review.id} 
      className={`${review.highlighted ? 'ring-2 ring-primary/20' : ''} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${className}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        {showServiceInfo && review.services && (
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              {review.services.image_url && (
                <div className="h-10 w-10 rounded-md overflow-hidden flex-shrink-0">
                  <img 
                    src={review.services.image_url} 
                    alt={review.services.name} 
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
              <div>
                <h4 className="font-semibold">{review.services.name}</h4>
                {review.services.location && (
                  <p className="text-sm text-muted-foreground">
                    {review.services.location}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-1 flex-shrink-0 ml-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-4 w-4 ${
                    i < review.rating
                      ? 'text-yellow-500 fill-yellow-500'
                      : 'text-muted-foreground'
                  }`}
                />
              ))}
              <span className="ml-1 text-sm font-medium">{review.rating}</span>
            </div>
          </div>
        )}

        {!showServiceInfo && (
          <div className="flex items-center gap-1 mb-3">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-4 w-4 ${
                  i < review.rating
                    ? 'text-yellow-500 fill-yellow-500'
                    : 'text-muted-foreground'
                }`}
              />
            ))}
            <span className="ml-1 text-sm font-medium">{review.rating}</span>
          </div>
        )}

        {review.comment && (
          <p className="text-sm text-muted-foreground mb-3">{review.comment}</p>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{format(new Date(review.created_at), 'MMM dd, yyyy')}</span>
          {review.highlighted && (
            <span className="bg-primary/10 text-primary px-2 py-1 rounded-full">
              Highlighted
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};